import PropTypes from "prop-types";
import { NavLink, useLocation } from "react-router-dom";
import useUtils from "@/hooks/useUtils";
import "./styles.scss";

/** Make a human readable label from a slug */
function labelFromSlug(slug) {
  try {
    const clean = decodeURIComponent(slug).replace(/[-_]+/g, " ").trim();
    return clean.replace(/\b\w/g, (m) => m.toUpperCase());
  } catch {
    return slug;
  }
}

/** Build automatic breadcrumbs from a pathname */
function buildAutoBreadcrumbs(pathname, rootLabel = "Home", rootUrl = "/") {
  const path = (pathname || "/").replace(/\/+$/, "") || "/";
  if (path === "/") return [{ title: rootLabel, url: rootUrl }];

  const parts = path.split("/").filter(Boolean);
  const crumbs = [{ title: rootLabel, url: rootUrl }];

  let acc = "";
  parts.forEach((seg, i) => {
    acc += `/${seg}`;
    crumbs.push({
      title: labelFromSlug(seg),
      url: i === parts.length - 1 ? "" : acc,
    });
  });

  return crumbs;
}

/** JSON-LD for BreadcrumbList */
function generateJSONLD(crumbs, origin) {
  const base = origin || (typeof window !== "undefined" ? window.location.origin : "");
  const items = crumbs
    .map((c) => ({
      ...c,
      abs: c.url ? (c.url.startsWith("http") ? c.url : `${base}${c.url}`) : "",
    }))
    .map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.title,
      ...(c.abs ? { item: c.abs } : {}),
    }));
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items,
  };
}

const BreadcrumbRevamp = ({
  showBreadcrumbs = true,
  rootLabel = "Home",
  rootUrl = "/",
  className = "hero-breadcrumbs",
}) => {
  const location = useLocation();
  const { convertToRelative } = useUtils();

  if (!showBreadcrumbs) return null;

  const crumbs = buildAutoBreadcrumbs(location?.pathname || "/", rootLabel, rootUrl);
  if (!crumbs.length) return null;

  const jsonLd = generateJSONLD(
    crumbs,
    typeof window !== "undefined" ? window.location.origin : ""
  );

  return (
    <nav className={className}>
      {crumbs.map((crumb, i) => {
        const last = i === crumbs.length - 1;
        const { title, url } = crumb;

        return (
          <span key={`${title}-${i}`} className="breadcrumb-item">
            {i === 0 && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="17"
                viewBox="0 0 16 17"
                fill="none"
                className="breadcrumb-icon"
              >
                <path
                  d="M6.71319 2.37925L2.09318 6.07924C1.57318 6.49257 1.23985 7.36593 1.35318 8.01926L2.23985 13.3259C2.39985 14.2726 3.30651 15.0392 4.26651 15.0392H11.7332C12.6865 15.0392 13.5999 14.2659 13.7599 13.3259L14.6465 8.01926C14.7532 7.36593 14.4199 6.49257 13.9065 6.07924L9.28651 2.38592C8.57318 1.81259 7.41986 1.81258 6.71319 2.37925Z"
                  stroke="#F6FCFF"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8.00016 10.8333C8.92064 10.8333 9.66683 10.0871 9.66683 9.16667C9.66683 8.24619 8.92064 7.5 8.00016 7.5C7.07969 7.5 6.3335 8.24619 6.3335 9.16667C6.3335 10.0871 7.07969 10.8333 8.00016 10.8333Z"
                  stroke="#F6FCFF"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}

            {!last && url ? (
              // Internal SPA navigation (no reload)
              <NavLink
                to={convertToRelative(url)}
                preventScrollReset
                className={({ isActive }) => (isActive ? "active" : "")}
              >
                {title}
              </NavLink>
            ) : (
              <span>{title}</span>
            )}

            {!last && crumbs.length > 1 && (
              <span className="breadcrumb-sep">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="17"
                  viewBox="0 0 16 17"
                  fill="none"
                >
                  <path
                    d="M4 8.49914V6.71913C4 4.51247 5.56667 3.6058 7.48 4.71247L9.02 5.6058L10.56 6.49913C12.4733 7.6058 12.4733 9.41247 10.56 10.5191L9.02 11.4125L7.48 12.3058C5.56667 13.3925 4 12.4925 4 10.2791V8.49914Z"
                    stroke="#F6FCFF"
                    strokeWidth="1.5"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            )}
          </span>
        );
      })}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    </nav>
  );
};

BreadcrumbRevamp.propTypes = {
  showBreadcrumbs: PropTypes.bool,
  /** Optional overrides for the first crumb (defaults to Home + "/") */
  rootLabel: PropTypes.string,
  rootUrl: PropTypes.string,
  /** Optional: pass a different class for styling reuse */
  className: PropTypes.string,
};

export default BreadcrumbRevamp;
